import React, { useState } from 'react';
import { useLanguage } from '../translations/LanguageContext';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Card } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';

const FirstConsultationForm = ({ onSubmit, onCancel }) => {
  const { t } = useLanguage();
  
  const [formData, setFormData] = useState({
    // Patient Information
    dob: '',
    gender: '',
    maritalStatus: '',
    spouseName: '',
    homeAddress: '',
    phoneHome: '',
    phoneCell: '',
    occupation: '',
    
    // Major Complaint
    majorComplaint: '',
    complaintSeverity: '',
    
    // Past Medical History
    pastMedicalHistory: [],
    
    // Medical Conditions & Surgeries
    medicalConditions: [{ year: '', condition: '' }],
    
    // Allergies
    allergies: '',
    
    // Occupational Concerns
    occupationalConcerns: [],
    occupationalConcernsOther: '',
    
    // Medications
    medications: [{ name: '', purpose: '', howLong: '', dose: '', lastDose: '' }],
    
    // Lifestyle
    tobaccoUse: '',
    alcoholUse: '',
    exerciseFrequency: '',
    
    // Females Only
    currentlyPregnant: '',
    lastPeriod: '',
    numPregnancies: '',
    numMiscarriages: '',
    birthControlMethod: '',
    menstrualCycleDetails: '',
    pmsSyndrome: '',
    lastPapTest: '',
    ageStoppedPeriods: '',
    menopauseSymptoms: ''
  });

  const pastMedicalHistoryOptions = [
    'AIDS', 'Anxiety Attacks', 'Asthma', 'Autoimmune Disease', 'Birth Defects',
    'Bladder Infections', 'Blood Disorders', 'Breast Tumor or Cancer', 'Bronchitis',
    'Cancer', 'Cirrhosis', 'Connective Tissue Disorders', 'Diabetes', 'Epilepsy',
    'Gallstones', 'Gastric/Duodenal Ulcers', 'German Measles', 'Heart Attack',
    'Heart Disease', 'Heart Murmur', 'Hepatitis', 'High Blood Pressure',
    'Intestinal Bleeding', 'Kidney Infection', 'Kidney Stones', 'Lupus',
    'Neurologic Disorders', 'Panic Attack', 'Paralysis', 'Rheumatoid Arthritis',
    'Seizures', 'Thyroid Problems', 'Tuberculosis', 'Varicose Veins'
  ];

  const occupationalConcernsOptions = [
    'Stress', 'Heavy Typing/Computer Use', 'Hazardous Substances', 'Heavy Lifting', 'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleCheckboxGroup = (field, value) => {
    const currentValues = formData[field];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    setFormData({ ...formData, [field]: newValues });
  };

  const addMedicalCondition = () => {
    setFormData({
      ...formData,
      medicalConditions: [...formData.medicalConditions, { year: '', condition: '' }]
    });
  };

  const updateMedicalCondition = (index, field, value) => {
    const updated = formData.medicalConditions.map((item, idx) =>
      idx === index ? { ...item, [field]: value } : item
    );
    setFormData({ ...formData, medicalConditions: updated });
  };

  const addMedication = () => {
    setFormData({
      ...formData,
      medications: [...formData.medications, { name: '', purpose: '', howLong: '', dose: '', lastDose: '' }]
    });
  };

  const updateMedication = (index, field, value) => {
    const updated = formData.medications.map((item, idx) =>
      idx === index ? { ...item, [field]: value } : item
    );
    setFormData({ ...formData, medications: updated });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Accordion type="single" collapsible defaultValue="patient-info" className="w-full">
        {/* Patient Information */}
        <AccordionItem value="patient-info">
          <AccordionTrigger className="heading-3">{t.firstConsultation.patientInfo.title}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 p-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dob">{t.firstConsultation.patientInfo.dob} *</Label>
                  <Input 
                    id="dob"
                    name="dob"
                    type="date"
                    value={formData.dob}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t.firstConsultation.patientInfo.gender} *</Label>
                  <RadioGroup value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                    <div className="flex gap-4">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="male" id="male" />
                        <Label htmlFor="male">{t.firstConsultation.patientInfo.male}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="female" id="female" />
                        <Label htmlFor="female">{t.firstConsultation.patientInfo.female}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="other" id="other" />
                        <Label htmlFor="other">{t.firstConsultation.patientInfo.other}</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label>{t.firstConsultation.patientInfo.maritalStatus}</Label>
                  <RadioGroup value={formData.maritalStatus} onValueChange={(value) => setFormData({ ...formData, maritalStatus: value })}>
                    <div className="flex gap-4">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="single" id="single" />
                        <Label htmlFor="single">{t.firstConsultation.patientInfo.single}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="married" id="married" />
                        <Label htmlFor="married">{t.firstConsultation.patientInfo.married}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="divorced" id="divorced" />
                        <Label htmlFor="divorced">{t.firstConsultation.patientInfo.divorced}</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="homeAddress">{t.firstConsultation.patientInfo.homeAddress} *</Label>
                <Textarea 
                  id="homeAddress"
                  name="homeAddress"
                  value={formData.homeAddress}
                  onChange={handleInputChange}
                  rows={2}
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phoneHome">{t.firstConsultation.patientInfo.phoneHome}</Label>
                  <Input 
                    id="phoneHome"
                    name="phoneHome"
                    type="tel"
                    value={formData.phoneHome}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phoneCell">{t.firstConsultation.patientInfo.phoneCell} *</Label>
                  <Input 
                    id="phoneCell"
                    name="phoneCell"
                    type="tel"
                    value={formData.phoneCell}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="occupation">{t.firstConsultation.patientInfo.occupation}</Label>
                <Input 
                  id="occupation"
                  name="occupation"
                  value={formData.occupation}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Major Complaint */}
        <AccordionItem value="major-complaint">
          <AccordionTrigger className="heading-3">{t.firstConsultation.majorComplaint.title}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 p-4">
              <div className="space-y-2">
                <Label htmlFor="majorComplaint">{t.firstConsultation.majorComplaint.describe} *</Label>
                <Textarea 
                  id="majorComplaint"
                  name="majorComplaint"
                  value={formData.majorComplaint}
                  onChange={handleInputChange}
                  rows={4}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>{t.firstConsultation.majorComplaint.intensity} *</Label>
                <RadioGroup value={formData.complaintSeverity} onValueChange={(value) => setFormData({ ...formData, complaintSeverity: value })}>
                  <div className="flex gap-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="severe" id="severe" />
                      <Label htmlFor="severe">{t.firstConsultation.majorComplaint.severe}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="moderate" id="moderate" />
                      <Label htmlFor="moderate">{t.firstConsultation.majorComplaint.moderate}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="slight" id="slight" />
                      <Label htmlFor="slight">{t.firstConsultation.majorComplaint.slight}</Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Past Medical History */}
        <AccordionItem value="medical-history">
          <AccordionTrigger className="heading-3">{t.firstConsultation.pastMedicalHistory.title}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 p-4">
              <Label>{t.firstConsultation.pastMedicalHistory.checkAll}</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {pastMedicalHistoryOptions.map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <Checkbox 
                      id={option}
                      checked={formData.pastMedicalHistory.includes(option)}
                      onCheckedChange={() => handleCheckboxGroup('pastMedicalHistory', option)}
                    />
                    <Label htmlFor={option} className="body-small cursor-pointer">
                      {t.firstConsultation.pastMedicalHistory.options?.[option] || option}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Medical Conditions & Surgeries */}
        <AccordionItem value="conditions-surgeries">
          <AccordionTrigger className="heading-3">{t.firstConsultation.medicalConditions.title}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 p-4">
              {formData.medicalConditions.map((condition, index) => (
                <div key={index} className="grid md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>{t.firstConsultation.medicalConditions.year}</Label>
                    <Input 
                      type="number"
                      placeholder={t.firstConsultation.medicalConditions.yearPlaceholder}
                      value={condition.year}
                      onChange={(e) => updateMedicalCondition(index, 'year', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-3">
                    <Label>{t.firstConsultation.medicalConditions.description}</Label>
                    <Input 
                      placeholder={t.firstConsultation.medicalConditions.descriptionPlaceholder}
                      value={condition.condition}
                      onChange={(e) => updateMedicalCondition(index, 'condition', e.target.value)}
                    />
                  </div>
                </div>
              ))}
              <button 
                type="button" 
                onClick={addMedicalCondition}
                className="btn-secondary"
              >
                {t.firstConsultation.medicalConditions.addCondition}
              </button>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Allergies */}
        <AccordionItem value="allergies">
          <AccordionTrigger className="heading-3">{t.firstConsultation.allergies.title}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 p-4">
              <div className="space-y-2">
                <Label htmlFor="allergies">{t.firstConsultation.allergies.label}</Label>
                <Textarea 
                  id="allergies"
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder={t.firstConsultation.allergies.placeholder}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Occupational Concerns */}
        <AccordionItem value="occupational">
          <AccordionTrigger className="heading-3">{t.firstConsultation.occupationalConcerns.title}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 p-4">
              <div className="space-y-3">
                {occupationalConcernsOptions.map((option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <Checkbox 
                      id={option}
                      checked={formData.occupationalConcerns.includes(option)}
                      onCheckedChange={() => handleCheckboxGroup('occupationalConcerns', option)}
                    />
                    <Label htmlFor={option} className="cursor-pointer">
                      {t.firstConsultation.occupationalConcerns.options?.[option] || option}
                    </Label>
                  </div>
                ))}
              </div>

              {formData.occupationalConcerns.includes('Other') && (
                <div className="space-y-2">
                  <Label htmlFor="occupationalConcernsOther">{t.firstConsultation.occupationalConcerns.specify}</Label>
                  <Input 
                    id="occupationalConcernsOther"
                    name="occupationalConcernsOther"
                    value={formData.occupationalConcernsOther}
                    onChange={handleInputChange}
                  />
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Medications */}
        <AccordionItem value="medications">
          <AccordionTrigger className="heading-3">{t.firstConsultation.currentMedications.title}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 p-4">
              {formData.medications.map((medication, index) => (
                <Card key={index} className="p-4 space-y-3">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t.firstConsultation.currentMedications.name}</Label>
                      <Input 
                        placeholder={t.firstConsultation.currentMedications.name}
                        value={medication.name}
                        onChange={(e) => updateMedication(index, 'name', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t.firstConsultation.currentMedications.purpose}</Label>
                      <Input 
                        placeholder={t.firstConsultation.currentMedications.purpose}
                        value={medication.purpose}
                        onChange={(e) => updateMedication(index, 'purpose', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>{t.firstConsultation.currentMedications.duration}</Label>
                      <Input 
                        placeholder={t.firstConsultation.currentMedications.durationPlaceholder}
                        value={medication.howLong}
                        onChange={(e) => updateMedication(index, 'howLong', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t.firstConsultation.currentMedications.dosage}</Label>
                      <Input 
                        placeholder={t.firstConsultation.currentMedications.dosagePlaceholder}
                        value={medication.dose}
                        onChange={(e) => updateMedication(index, 'dose', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t.firstConsultation.currentMedications.lastDose}</Label>
                      <Input 
                        type="date"
                        value={medication.lastDose}
                        onChange={(e) => updateMedication(index, 'lastDose', e.target.value)}
                      />
                    </div>
                  </div>
                </Card>
              ))}
              <button 
                type="button" 
                onClick={addMedication}
                className="btn-secondary"
              >
                {t.firstConsultation.currentMedications.addMedication}
              </button>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Lifestyle */}
        <AccordionItem value="lifestyle">
          <AccordionTrigger className="heading-3">{t.firstConsultation.lifestyle.title}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-6 p-4">
              <div className="space-y-2">
                <Label>{t.firstConsultation.lifestyle.tobacco}</Label>
                <RadioGroup value={formData.tobaccoUse} onValueChange={(value) => setFormData({ ...formData, tobaccoUse: value })}>
                  <div className="flex gap-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="tobacco-yes" />
                      <Label htmlFor="tobacco-yes">{t.firstConsultation.lifestyle.yes}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="tobacco-no" />
                      <Label htmlFor="tobacco-no">{t.firstConsultation.lifestyle.no}</Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label>{t.firstConsultation.lifestyle.alcohol}</Label>
                <RadioGroup value={formData.alcoholUse} onValueChange={(value) => setFormData({ ...formData, alcoholUse: value })}>
                  <div className="flex gap-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="alcohol-yes" />
                      <Label htmlFor="alcohol-yes">{t.firstConsultation.lifestyle.yes}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="alcohol-no" />
                      <Label htmlFor="alcohol-no">{t.firstConsultation.lifestyle.no}</Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label>{t.firstConsultation.lifestyle.exercise}</Label>
                <RadioGroup value={formData.exerciseFrequency} onValueChange={(value) => setFormData({ ...formData, exerciseFrequency: value })}>
                  <div className="flex gap-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="regular" id="exercise-regular" />
                      <Label htmlFor="exercise-regular">{t.firstConsultation.lifestyle.regular}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="occasional" id="exercise-occasional" />
                      <Label htmlFor="exercise-occasional">{t.firstConsultation.lifestyle.occasional}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="none" id="exercise-none" />
                      <Label htmlFor="exercise-none">{t.firstConsultation.lifestyle.none}</Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Females Only Section */}
        {formData.gender === 'female' && (
          <AccordionItem value="females-only">
            <AccordionTrigger className="heading-3">{t.firstConsultation.femaleHealth.title}</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 p-4">
                <div className="space-y-2">
                  <Label>{t.firstConsultation.femaleHealth.pregnant}</Label>
                  <RadioGroup value={formData.currentlyPregnant} onValueChange={(value) => setFormData({ ...formData, currentlyPregnant: value })}>
                    <div className="flex gap-4">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="pregnant-yes" />
                        <Label htmlFor="pregnant-yes">{t.firstConsultation.lifestyle.yes}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="pregnant-no" />
                        <Label htmlFor="pregnant-no">{t.firstConsultation.lifestyle.no}</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="lastPeriod">{t.firstConsultation.femaleHealth.lastPeriod}</Label>
                    <Input 
                      id="lastPeriod"
                      name="lastPeriod"
                      type="date"
                      value={formData.lastPeriod}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="numPregnancies">{t.firstConsultation.femaleHealth.numPregnancies}</Label>
                    <Input 
                      id="numPregnancies"
                      name="numPregnancies"
                      type="number"
                      value={formData.numPregnancies}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="numMiscarriages">{t.firstConsultation.femaleHealth.numMiscarriages}</Label>
                    <Input 
                      id="numMiscarriages"
                      name="numMiscarriages"
                      type="number"
                      value={formData.numMiscarriages}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="birthControlMethod">{t.firstConsultation.femaleHealth.birthControl}</Label>
                    <Input 
                      id="birthControlMethod"
                      name="birthControlMethod"
                      value={formData.birthControlMethod}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="menstrualCycleDetails">{t.firstConsultation.femaleHealth.menstrualCycle}</Label>
                  <Textarea 
                    id="menstrualCycleDetails"
                    name="menstrualCycleDetails"
                    value={formData.menstrualCycleDetails}
                    onChange={handleInputChange}
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t.firstConsultation.femaleHealth.pms}</Label>
                  <RadioGroup value={formData.pmsSyndrome} onValueChange={(value) => setFormData({ ...formData, pmsSyndrome: value })}>
                    <div className="flex gap-4">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="pms-yes" />
                        <Label htmlFor="pms-yes">{t.firstConsultation.lifestyle.yes}</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="pms-no" />
                        <Label htmlFor="pms-no">{t.firstConsultation.lifestyle.no}</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="lastPapTest">{t.firstConsultation.femaleHealth.lastPapTest}</Label>
                    <Input 
                      id="lastPapTest"
                      name="lastPapTest"
                      type="date"
                      value={formData.lastPapTest}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ageStoppedPeriods">{t.firstConsultation.femaleHealth.ageStoppedPeriods}</Label>
                    <Input 
                      id="ageStoppedPeriods"
                      name="ageStoppedPeriods"
                      type="number"
                      placeholder={t.firstConsultation.femaleHealth.ageStoppedPlaceholder}
                      value={formData.ageStoppedPeriods}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="menopauseSymptoms">{t.firstConsultation.femaleHealth.menopauseSymptoms}</Label>
                  <Textarea 
                    id="menopauseSymptoms"
                    name="menopauseSymptoms"
                    value={formData.menopauseSymptoms}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder={t.firstConsultation.femaleHealth.menopausePlaceholder}
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      {/* Form Actions */}
      <div className="flex gap-4 justify-end">
        <button type="button" onClick={onCancel} className="btn-secondary">
          {t.firstConsultation.buttons.cancel}
        </button>
        <button type="submit" className="btn-primary">
          {t.firstConsultation.buttons.submit}
        </button>
      </div>
    </form>
  );
};

export default FirstConsultationForm;
